'use strict';

import { dwService } from 'amb/services/dw.service';

/**
 * Client Tab Id for multi-cart isolation: mint, read, and attach `mcTab`.
 */
export const tabIdentity = {
  /**
   * Site preference snapshot from clientData (page load).
   *
   * @readonly
   *
   * @type {boolean}
   */
  TAB_ISOLATION_ENABLED: !!dwService.preference('tabIsolationEnabled'),

  /**
   * Site preference snapshot: enforce client/server Tab Isolation mismatch (409 + modal).
   *
   * @readonly
   *
   * @type {boolean}
   */
  TAB_ISOLATION_STRICT: !!dwService.preference('tabIsolationStrict'),

  /**
   * @private
   * @readonly
   */
  TAB_ID_KEY: 'mcTab',

  /**
   * Prefix on `window.name` only (`sessionStorage` stores the raw id).
   *
   * @private
   * @readonly
   */
  TAB_PREFIX: 'mc-',

  /**
   * Raw Client Tab Id shape (10 hex chars).
   *
   * @private
   * @readonly
   */
  TAB_ID_PATTERN: /^[a-f0-9]{10}$/i,

  /**
   * True when configurator builds are isolated per browser tab.
   *
   * @returns {boolean}
   */
  isTabIsolation() {
    return this.TAB_ISOLATION_ENABLED;
  },

  /**
   * True when AJAX should treat Tab Isolation mismatch as a blocking error.
   *
   * @returns {boolean}
   */
  isTabIsolationStrict() {
    return this.TAB_ISOLATION_STRICT;
  },

  /**
   * Read tab id without minting one.
   *
   * @returns {string|null}
   */
  peekTabId() {
    const windowTabId = this.getWindowTabId();

    try {
      const id = sessionStorage.getItem(this.TAB_ID_KEY);

      if (id && id === windowTabId) {
        return id;
      }
    } catch (e) {
      return this.getWindowTabId();
    }

    return null;
  },

  /**
   * Returns the stable per-tab id, generating and persisting it on first use.
   *
   * @returns {string}
   */
  getTabId() {
    let id;

    try {
      id = sessionStorage.getItem(this.TAB_ID_KEY);

      if (!id || id !== this.getWindowTabId()) {
        id = this.generateId();

        this.setWindowTabId(id);
        sessionStorage.setItem(this.TAB_ID_KEY, id);
      }
    } catch (e) {
      // sessionStorage unavailable (e.g. hardened privacy mode): reuse a valid
      // window.name id when present; otherwise mint a per-page-load id.
      const windowTabId = this.getWindowTabId();

      if (windowTabId) {
        return windowTabId;
      }

      id = this.generateId();
      this.setWindowTabId(id);
    }

    return id;
  },

  /**
   * Appends `mcTab` to a URL (for full-page navigations / GET links).
   *
   * @param {string} url
   *
   * @returns {string}
   */
  appendTabId(url) {
    const tabId = this.peekTabId();

    if (!url || !tabId) {
      return url;
    }

    try {
      const parsedUrl = new URL(url, window.location.origin);

      parsedUrl.searchParams.set(this.TAB_ID_KEY, tabId);

      if (/^https?:\/\//i.test(url)) {
        return parsedUrl.toString();
      }

      return parsedUrl.pathname + parsedUrl.search + parsedUrl.hash;
    } catch (e) {
      return url;
    }
  },

  /**
   * Parse Client Tab Id from `window.name` (`mc-{id}` only).
   *
   * @private
   *
   * @returns {string|null}
   */
  getWindowTabId() {
    const name = window.name;

    if (!name) {
      return null;
    }

    if (name.indexOf(this.TAB_PREFIX) === 0) {
      const id = name.substring(this.TAB_PREFIX.length);

      return this.TAB_ID_PATTERN.test(id) ? id : null;
    }

    return null;
  },

  /**
   * Persist Client Tab Id on `window.name` as `mc-{id}`.
   *
   * @private
   *
   * @param {string} id
   */
  setWindowTabId(id) {
    window.name = this.TAB_PREFIX + id;
  },

  /**
   * Generates a short, random 10-character hexadecimal identifier.
   *
   * @private
   *
   * @returns {string} A fixed-length 10-character hex ID (e.g., '1a2b3c4d5e').
   */
  generateId() {
    const bytes = new Uint8Array(5);

    if (typeof window.crypto?.getRandomValues === 'function') {
      window.crypto.getRandomValues(bytes);
    } else {
      for (let i = 0; i < bytes.length; i++) {
        bytes[i] = (Math.random() * 256) | 0;
      }
    }

    return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
  }
};
